import _uniqueId from 'lodash/uniqueId'
import React, { useEffect, useState, useCallback, useContext } from 'react'

import globalConfirmation from 'live/services/global-confirmation'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { EditorTopBar, IError, IListItemError } from '../../../generic'
import { useDimensions } from '../../../hooks'
import { updateDiagram, getDiagrams } from '../../../services'
import { IHazard, ITopEvent, IConsequence, IThreat, IDiagram } from '../../../types'
import { checkIsValidFrequencyRange } from '../../../utils'
import { BowtieSettingsRouteContext, IBowtieSettingsRouteContext, IQueryParamsEdit } from '../../BowtieSettingWrapper'
import {
    DiagramEditorSvg,
    ConsequenceThreatList,
    HazardModal,
    ThreatConsequenceModal,
    TopEventModal
} from './components'
import { RiskDiagramEditorDiv } from './styles'

export type IDiagramConsequence = Omit<IConsequence, 'barriers' | 'dependentBarriers' | 'thresholdFrequency'> & {
    uid?: string
    thresholdFrequency: string
}
export type IDiagramThreat = Omit<IThreat, 'dependentBarriers' | 'barriers' | 'initialFrequency'> & {
    uid?: string
    initialFrequency: string
}
export type IDiagramTopEvent = Omit<ITopEvent, 'consequences' | 'threats' | 'hazard' | 'dependentBarriers'>
export type IDiagramTypes = 'HAZARD' | 'TOPEVENT' | 'THREAT' | 'CONSEQUENCE'
export type IDiagramTypesAux = 'THREAT' | 'CONSEQUENCE'
type ISideAux = IDiagramThreat | IDiagramConsequence

export const RiskDiagramEditor: React.FC = () => {
    const dimensions = useDimensions()
    const [hazard, setHazard] = useState<IHazard>()
    const [topEvent, setTopEvent] = useState<IDiagramTopEvent>()
    const [threats, setThreats] = useState<IDiagramThreat[]>([])
    const [consequences, setConsequences] = useState<IDiagramConsequence[]>([])
    const [itemEditIndex, setEditIndex] = useState(-1)
    const [typeToEdit, setTypeToEdit] = useState<IDiagramTypes>()
    const [showEntityModal, setShowEntityModal] = useState(false)
    const [needToUpdate, setNeedToUpdate] = useState(false)
    const [errors, setErrors] = useState<IListItemError[]>([])

    const { params, setParams } = useContext<IBowtieSettingsRouteContext<IQueryParamsEdit>>(BowtieSettingsRouteContext)

    const [saveLoading, setSaveLoading] = useState(false)

    const handleGetData = async (): Promise<void> => {
        try {
            const data = await getDiagrams(params.id)
            setTopEvent(data?.topEvent)
            setHazard(data?.hazard)
            setThreats(
                ((data?.topEvent?.threats as unknown) as IDiagramThreat[]) || [
                    { id: undefined, name: undefined, initialFrequency: undefined, uid: _uniqueId() }
                ]
            )
            setConsequences(
                ((data?.topEvent?.consequences as unknown) as IDiagramConsequence[]) || [
                    { id: undefined, name: undefined, thresholdFrequency: undefined, uid: _uniqueId() }
                ]
            )
        } catch (err) {
            globalNotification.error(i18n(err.message ?? 'An error occurred on get diagram.'))
        }
    }

    const handleAddThreatConsequenceClick = (type: IDiagramTypesAux): void => {
        if (type === 'THREAT') {
            setThreats([...threats, { id: null, uid: _uniqueId(), name: null, initialFrequency: null }])
            setTypeToEdit('THREAT')
            setEditIndex(threats.length)
            setShowEntityModal(true)
        } else if (type === 'CONSEQUENCE') {
            setConsequences([...consequences, { id: null, uid: _uniqueId(), name: null, thresholdFrequency: null }])
            setTypeToEdit('CONSEQUENCE')
            setEditIndex(consequences.length)
            setShowEntityModal(true)
        }
    }

    const handleDeleteThreatConsequenceClick = (type: IDiagramTypesAux, index: number): void => {
        let newArray = []
        if (type === 'THREAT') {
            newArray = [...threats]
            newArray.splice(index, 1)
            setThreats(newArray)
        } else if (type === 'CONSEQUENCE') {
            newArray = [...consequences]
            newArray.splice(index, 1)
            setConsequences(newArray)
        }
        setNeedToUpdate(true)
    }

    const handleShowEntityEditorModal = useCallback(
        (type: IDiagramTypes, item?: IDiagramTopEvent | IHazard | IDiagramConsequence | IDiagramThreat): void => {
            if (type === 'THREAT') {
                setEditIndex(threats.indexOf(item as IDiagramThreat))
            } else if (type === 'CONSEQUENCE') {
                setEditIndex(consequences.indexOf(item as IDiagramConsequence))
            }

            setTypeToEdit(type)
            setShowEntityModal(true)
        },
        [consequences, threats]
    )

    const handleCloseEntityModal = (): void => {
        setEditIndex(null)
        setTypeToEdit(null)
        setShowEntityModal(false)
    }

    const handleSave = async (): Promise<void> => {
        try {
            setSaveLoading(true)
            if (consequences.length === 0 || threats.length === 0) {
                throw new Error(i18n('A risk diagram must have at least one threat and at least one consequence'))
            }

            const bowtieTemplate = {
                id: params.id,
                hazard,
                topEvent: {
                    id: topEvent.id,
                    name: topEvent.name,
                    consequences: consequences,
                    threats: threats
                }
            }

            const savedData = await updateDiagram((bowtieTemplate as unknown) as IDiagram)
            const { threats: newThreats, consequences: newConsequences } = savedData?.topEvent
            setThreats(((newThreats as unknown) as IDiagramThreat[]) ?? [])
            setConsequences(((newConsequences as unknown) as IDiagramConsequence[]) ?? [])

            globalNotification.success(i18n('Diagram saved successfully.'))
            setNeedToUpdate(false)
        } catch (err) {
            globalNotification.error(i18n(err.message ?? 'Error on save diagram.'))
        } finally {
            setSaveLoading(false)
        }
    }

    const handleDiagramListChange = (type: IDiagramTypes, newList?: ISideAux[]): void => {
        if (type === 'THREAT') {
            setThreats((newList as unknown) as IDiagramThreat[])
        } else if (type === 'CONSEQUENCE') {
            setConsequences((newList as unknown) as IDiagramConsequence[])
        }
        setNeedToUpdate(true)
    }

    const mountErrorList = (): IListItemError[] => {
        const items: IListItemError[] = []

        for (const [idx, threat] of threats.entries()) {
            const threatErrors: string[] = []

            if (!threat.name || threat.name.trim().length === 0) {
                threatErrors.push(i18n('a valid name'))
            }

            if (!checkIsValidFrequencyRange(threat.initialFrequency)) {
                threatErrors.push(i18n('a valid initial frequency'))
            }

            if (threatErrors.length > 0) {
                items.push({
                    type: 'THREAT',
                    errors: `${i18n('Missing')} ${threatErrors.join(', ')}`,
                    name: threat.name?.trim()?.length > 0 ? threat.name : `${i18n('THREAT')} ${idx + 1}`,
                    onEdit: () => handleShowEntityEditorModal('THREAT', threat)
                })
            }
        }

        for (const [idx, consequence] of consequences.entries()) {
            const consequenceErrors: string[] = []

            if (!consequence.name || consequence.name.trim().length === 0) {
                consequenceErrors.push(i18n('a valid name'))
            }

            if (!checkIsValidFrequencyRange(consequence.thresholdFrequency)) {
                consequenceErrors.push(i18n('a valid threshold frequency'))
            }
            if (consequenceErrors.length > 0) {
                items.push({
                    type: 'CONSEQUENCE',
                    errors: `${i18n('Missing')} ${consequenceErrors.join(', ')}`,
                    name: consequence.name?.trim()?.length > 0 ? consequence.name : `${i18n('CONSEQUENCE')} ${idx + 1}`,
                    onEdit: () => handleShowEntityEditorModal('CONSEQUENCE', consequence)
                })
            }
        }

        for (const [idx, consequence] of consequences.entries()) {
            const consequenceFrequencyErrors: string[] = []

            if (!checkIsValidFrequencyRange(consequence.thresholdFrequency)) {
                consequenceFrequencyErrors.push(i18n('a valid threshold frequency'))
            }
            if (consequenceFrequencyErrors.length > 0) {
                items.push({
                    type: 'CONSEQUENCE',
                    errors: `${i18n('Missing')} ${consequenceFrequencyErrors.join(', ')}`,
                    name: consequence.name?.trim()?.length > 0 ? consequence.name : `${i18n('CONSEQUENCE')} ${idx + 1}`,
                    onEdit: () => handleShowEntityEditorModal('CONSEQUENCE', consequence)
                })
            }
        }

        if (topEvent?.name?.trim()?.length === 0) {
            items.push({
                type: 'TOPEVENT',
                errors: i18n('Missing a valid name'),
                name: i18n('TOP EVENT'),
                onEdit: () => handleShowEntityEditorModal('TOPEVENT', topEvent)
            })
        }

        if (hazard?.name?.trim()?.length === 0) {
            items.push({
                type: 'HAZARD',
                errors: i18n('Missing a valid name'),
                name: i18n('HAZARD'),
                onEdit: () => handleShowEntityEditorModal('HAZARD', hazard)
            })
        }

        return items
    }

    useEffect(() => {
        handleGetData()
    }, [params.id]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setErrors(mountErrorList())
    }, [topEvent, hazard, threats, consequences]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <EditorTopBar
                title={[
                    {
                        text: hazard?.name ?? 'HAZARD',
                        maxWidth: '42.5%'
                    },
                    {
                        text: topEvent?.name ?? 'Top Event',
                        maxWidth: '42.5%'
                    },
                    {
                        text: hazard?.operation?.description ?? 'Operation',
                        maxWidth: '15%'
                    }
                ]}
                handleGoBack={() => {
                    if (needToUpdate) {
                        globalConfirmation.warn(
                            i18n('Are you sure you want to leave without saving your work?'),
                            () => setParams({ type: 'list', tab: 'diagrams' }),
                            { btnText: i18n('Leave without saving') }
                        )
                    } else {
                        setParams({ type: 'list', tab: 'diagrams' })
                    }
                }}
                handleSave={handleSave}
                saveLoading={saveLoading}
                disabled={showEntityModal || !needToUpdate || errors.length > 0}
            />
            <RiskDiagramEditorDiv>
                <div className="diagram-template">
                    <DiagramEditorSvg
                        height={dimensions.height}
                        width={dimensions.width - 410}
                        consequences={consequences}
                        threats={threats}
                        topEvent={topEvent?.name ?? 'topEvent'}
                        hazard={hazard}
                        onHazardClick={() => handleShowEntityEditorModal('HAZARD')}
                        onTopEventClick={() => handleShowEntityEditorModal('TOPEVENT')}
                        onThreatConsequenceClick={handleShowEntityEditorModal}
                        onAddButtonClick={handleAddThreatConsequenceClick}
                        onDeleteClick={handleDeleteThreatConsequenceClick}
                        editing={typeToEdit ? { type: typeToEdit, index: itemEditIndex } : undefined}
                    />
                </div>
                <div className="diagram-list">
                    <ConsequenceThreatList
                        errors={errors}
                        hazard={hazard}
                        topEvent={topEvent}
                        threats={threats}
                        consequences={consequences}
                        onListClick={handleShowEntityEditorModal}
                        onListDeleteClick={handleDiagramListChange}
                    />
                </div>

                <HazardModal
                    handleClose={handleCloseEntityModal}
                    isOpen={typeToEdit === 'HAZARD' && showEntityModal}
                    item={hazard}
                    handleDescriptionChange={k => {
                        setHazard(old => ({ ...old, name: k }))
                        setNeedToUpdate(true)
                    }}
                    handleOperationChange={(id, description) => {
                        setHazard(old => ({ ...old, operation: { id, description } }))
                        setNeedToUpdate(true)
                    }}
                />

                <TopEventModal
                    isOpen={typeToEdit === 'TOPEVENT' && showEntityModal}
                    handleClose={handleCloseEntityModal}
                    item={topEvent}
                    handleDescriptionChange={name => {
                        setTopEvent(old => ({ ...old, name }))
                        setNeedToUpdate(true)
                    }}
                />

                <ThreatConsequenceModal
                    isOpen={typeToEdit === 'THREAT' && showEntityModal}
                    handleClose={handleCloseEntityModal}
                    borderColor="#3866B1"
                    title="THREAT"
                    item={threats[itemEditIndex]}
                    handleDescriptionChange={e => {
                        const newThreat = [...threats]
                        newThreat[itemEditIndex].name = e

                        setThreats(newThreat)
                        setNeedToUpdate(true)
                    }}
                    handleInitialFrequencyChange={e => {
                        const newThreat = [...threats]
                        newThreat[itemEditIndex].initialFrequency = e

                        setThreats(newThreat)
                        setNeedToUpdate(true)
                    }}
                />

                <ThreatConsequenceModal
                    isOpen={typeToEdit === 'CONSEQUENCE' && showEntityModal}
                    handleClose={handleCloseEntityModal}
                    borderColor="#8768A3"
                    title="CONSEQUENCE"
                    item={consequences[itemEditIndex]}
                    handleDescriptionChange={e => {
                        const newConsequences = [...consequences]
                        newConsequences[itemEditIndex].name = e

                        setConsequences(newConsequences)
                        setNeedToUpdate(true)
                    }}
                    handleThresholdFrequencyChange={e => {
                        const newConsequence = [...consequences]
                        newConsequence[itemEditIndex].thresholdFrequency = e
                        setConsequences(newConsequence)
                        setNeedToUpdate(true)
                    }}
                />
            </RiskDiagramEditorDiv>
        </>
    )
}
